import { Controller } from "@hotwired/stimulus"
import { t } from "../i18n.js"

const HEART_OUTLINE = `
  <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 8.25C21 5.76472 18.9013 3.75 16.3125 3.75C14.3769 3.75 12.7153 4.87628 12 6.48342C11.2847 4.87628 9.62312 3.75 7.6875 3.75C5.09867 3.75 3 5.76472 3 8.25C3 15.4706 12 20.25 12 20.25C12 20.25 21 15.4706 21 8.25Z"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
`

const HEART_SOLID = `
  <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.645 20.9107L11.6384 20.9072L11.6158 20.8949C11.5965 20.8844 11.5689 20.8693 11.5336 20.8496C11.4629 20.8101 11.3612 20.7524 11.233 20.6769C10.9765 20.5261 10.6132 20.3039 10.1785 20.015C9.31074 19.4381 8.15122 18.5901 6.9886 17.5063C4.68781 15.3615 2.25 12.1751 2.25 8.25C2.25 5.32194 4.7136 3 7.6875 3C9.43638 3 11.0023 3.79909 12 5.0516C12.9977 3.79909 14.5636 3 16.3125 3C19.2864 3 21.75 5.32194 21.75 8.25C21.75 12.1751 19.3122 15.3615 17.0114 17.5063C15.8488 18.5901 14.6893 19.4381 13.8215 20.015C13.3868 20.3039 13.0235 20.5261 12.767 20.6769C12.6388 20.7524 12.5371 20.8101 12.4664 20.8496C12.4311 20.8693 12.4035 20.8844 12.3842 20.8949L12.3616 20.9072L12.355 20.9107L12.3523 20.9121C12.1323 21.0289 11.8677 21.0289 11.6477 20.9121L11.645 20.9107Z"
    />
  </svg>
`

const ACTIVE_CLASSES = ["bg-amber-100", "ring-1", "ring-amber-300", "rounded-sm"]
const SAVED_CLASSES = ["bg-rose-50", "ring-1", "ring-rose-300", "rounded-sm"]
const BUSY_CLASSES = ["pointer-events-none", "opacity-70"]
const SELECT_PROMPT = () => t('select_favorite', "Select text, then press the heart to save a favorite.")

export default class extends Controller {
  static targets = ["favoriteButton", "completion", "progressBar"]
  static values = { docId: Number, granularity: String, favoriteEnabled: Boolean, basePath: String }

  initialize() {
    this.selection = null
    this.selectionSegment = null
    this.selectionControls = null
  }

  csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  connect() {
    this.bindKeys = this.handleKeys.bind(this)
    document.addEventListener("keydown", this.bindKeys)
    this.hasReachedEnd = false
    this.updateNavState()
    this.updateFavoriteState()
  }

  disconnect() {
    document.removeEventListener("keydown", this.bindKeys)
    this.clearSelectionState()
  }

  granularity() {
    return this.granularityValue || "paragraph"
  }

  currentIndex() {
    return parseInt(this.element.querySelector("#mf-segment [data-index]")?.dataset.index || "0", 10)
  }

  // Reader endpoints live under /documents/:id for signed-in readers and under
  // /r/:share_token for anonymous share links. Both speak the same API.
  basePath() {
    return this.hasBasePathValue && this.basePathValue
      ? this.basePathValue
      : `/documents/${this.docIdValue}`
  }

  currentSegment() {
    return this.element.querySelector("#mf-segment [data-index]")
  }

  async load(i) {
    this.clearSelectionState()

    const g = this.granularity()
    const resp = await fetch(`${this.basePath()}/segment?i=${i}&g=${g}`, {
      credentials: "same-origin",
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
    })
    if (!resp.ok) return
    const html = await resp.text()
    this.element.querySelector("#mf-segment").innerHTML = html

    const count = parseInt(this.element.querySelector("#mf-total").textContent, 10)
    this.element.querySelector("#mf-counter").textContent = (i + 1)
    const pct = Math.min(100, Math.max(0, ((i + 1) * 100.0 / count)))
    this.element.querySelector("#mf-progress").style.width = `${pct}%`
    if (this.hasProgressBarTarget) this.progressBarTarget.setAttribute("aria-valuenow", Math.round(pct))

    const progressResponse = await fetch(`${this.basePath()}/progress`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrf() },
      body: JSON.stringify({ i, g })
    })
    if (progressResponse.ok) {
      const result = await progressResponse.json()
      if (result.completed && this.hasCompletionTarget) {
        this.completionTarget.innerHTML = result.completion_html
        // Reaching the last segment is a direct result of the reader's own
        // Next click/keypress, so — unlike the scroll-triggered completion in
        // reader_position_controller.js — landing focus on the new heading
        // here is an expected, not surprising, continuation of that action.
        this.completionTarget.querySelector("#reader-completion-title")?.focus()
      }
    }

    this.updateNavState()
    this.updateFavoriteState()
  }

  next() {
    const i = this.currentIndex() + 1
    const count = parseInt(this.element.querySelector("#mf-total").textContent, 10)
    if (i < count) this.load(i)
  }

  prev() {
    const i = this.currentIndex() - 1
    if (i >= 0) this.load(i)
  }

  react(e) {
    const reaction = e.currentTarget.dataset.reaction
    const i = this.currentIndex()
    const g = this.granularity()
    fetch(`${this.basePath()}/react`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrf() },
      body: JSON.stringify({ i, g, reaction })
    }).then(resp => { if (resp.ok) this.next() })
  }

  toggleFavorite(event) {
    if (!this.favoriteEnabledValue || !this.hasFavoriteButtonTarget) return
    event.preventDefault()

    const button = this.favoriteButtonTarget
    const segment = this.currentSegment()
    if (!segment) return

    const favoritable = segment.dataset.favoritable === "true"
    if (!favoritable) {
      button.setAttribute("disabled", "disabled")
      button.setAttribute("aria-disabled", "true")
      button.title = "Unavailable"
      return
    }

    if (button.dataset.busy === "true") return

    if (segment.dataset.favorited === "true") {
      this.removeFavorite(segment, button)
      return
    }

    const selection = this.extractSelection(segment)
    if (!selection) {
      window.alert(SELECT_PROMPT())
      return
    }

    this.beginSelection(segment, selection)
  }

  async removeFavorite(segment, button) {
    this.clearSelectionState()
    button.dataset.busy = "true"
    button.setAttribute("aria-busy", "true")
    BUSY_CLASSES.forEach(cls => button.classList.add(cls))

    const payload = {
      i: this.currentIndex(),
      g: this.granularity(),
      favorited: false
    }

    const quoteId = segment.dataset.favoriteQuoteId
    if (quoteId) payload.quote_id = quoteId

    const start = this.normalizeId(segment.dataset.favoriteStart)
    const end = this.normalizeId(segment.dataset.favoriteEnd)
    if (start && end) payload.selection = { start_sent_id: start, end_sent_id: end }

    try {
      const resp = await fetch(`${this.basePath()}/favorite`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrf()
        },
        body: JSON.stringify(payload)
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.error || "Unable to update favorite")
      }
      await resp.json()
      segment.dataset.favorited = "false"
      segment.dataset.favoriteQuoteId = ""
      segment.dataset.favoriteStart = ""
      segment.dataset.favoriteEnd = ""
      this.clearSavedHighlight(segment)
      this.renderFavoriteButton(false)
    } catch (error) {
      console.error(error)
      window.alert(error.message || t('favorite_failed', "Unable to update favorite. Please try again."))
    } finally {
      delete button.dataset.busy
      button.removeAttribute("aria-busy")
      BUSY_CLASSES.forEach(cls => button.classList.remove(cls))
      this.updateFavoriteState()
    }
  }

  beginSelection(segment, selection) {
    this.clearSelectionState()
    this.selectionSegment = segment
    this.selection = { startId: selection.startId, endId: selection.endId }

    this.clearSavedHighlight(segment)
    this.clearActiveHighlight(segment)
    this.highlightRange(segment, selection.startId, selection.endId, ACTIVE_CLASSES)
    this.renderSelectionControls(segment)
    window.getSelection()?.removeAllRanges()
  }

  renderSelectionControls(segment) {
    if (!this.selection || !this.selectionSegment) return
    if (this.selectionControls) this.selectionControls.remove()

    const endElement = this.findSentenceById(segment, this.selection.endId)
    if (!endElement) return

    const container = document.createElement("span")
    container.className = "ml-2 inline-flex items-center gap-1 text-xs align-middle"

    if (this.canExtendSelection()) {
      container.appendChild(this.buildControlButton("➕", "Add sentence to favorite", () => this.extendSelection()))
    }

    if (this.rangeLength() > 1) {
      container.appendChild(this.buildControlButton("➖", "Remove last sentence", () => this.shrinkSelection()))
    }

    container.appendChild(this.buildControlButton("✅", "Save favorite", () => this.saveSelection()))
    container.appendChild(this.buildControlButton("❌", "Cancel", () => this.cancelSelection()))

    this.selectionControls = container
    endElement.appendChild(container)
  }

  buildControlButton(symbol, label, handler) {
    const button = document.createElement("button")
    button.type = "button"
    button.textContent = symbol
    button.className = "px-1"
    button.setAttribute("aria-label", label)
    button.addEventListener("click", (event) => {
      event.preventDefault()
      handler()
    })
    return button
  }

  extendSelection() {
    if (!this.selectionSegment || !this.selection) return
    const sentences = this.sentencesForSegment(this.selectionSegment)
    const endIndex = this.findSentenceIndex(sentences, this.selection.endId)
    if (endIndex === -1 || endIndex >= sentences.length - 1) return

    this.selection.endId = sentences[endIndex + 1].dataset.sentId
    this.clearActiveHighlight(this.selectionSegment)
    this.highlightRange(this.selectionSegment, this.selection.startId, this.selection.endId, ACTIVE_CLASSES)
    this.renderSelectionControls(this.selectionSegment)
  }

  shrinkSelection() {
    if (!this.selectionSegment || !this.selection) return
    const sentences = this.sentencesForSegment(this.selectionSegment)
    const startIndex = this.findSentenceIndex(sentences, this.selection.startId)
    const endIndex = this.findSentenceIndex(sentences, this.selection.endId)
    if (startIndex === -1 || endIndex === -1) return

    if (startIndex === endIndex) {
      this.cancelSelection()
      return
    }

    this.selection.endId = sentences[endIndex - 1].dataset.sentId
    this.clearActiveHighlight(this.selectionSegment)
    this.highlightRange(this.selectionSegment, this.selection.startId, this.selection.endId, ACTIVE_CLASSES)
    this.renderSelectionControls(this.selectionSegment)
  }

  async saveSelection() {
    if (!this.selectionSegment || !this.selection || !this.hasFavoriteButtonTarget) return

    const button = this.favoriteButtonTarget
    if (button.dataset.busy === "true") return

    button.dataset.busy = "true"
    button.setAttribute("aria-busy", "true")
    BUSY_CLASSES.forEach(cls => button.classList.add(cls))

    const payload = {
      i: this.currentIndex(),
      g: this.granularity(),
      favorited: true,
      selection: {
        start_sent_id: this.selection.startId,
        end_sent_id: this.selection.endId
      }
    }

    const segment = this.selectionSegment

    try {
      const resp = await fetch(`${this.basePath()}/favorite`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrf()
        },
        body: JSON.stringify(payload)
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.error || "Unable to update favorite")
      }
      const data = await resp.json()
      segment.dataset.favorited = String(data.favorited === true)
      segment.dataset.favoriteQuoteId = data.quote_id || ""
      segment.dataset.favoriteStart = data.start_sent_id || this.selection.startId
      segment.dataset.favoriteEnd = data.end_sent_id || this.selection.endId
      this.renderFavoriteButton(true)
      this.clearSelectionState()
      this.applySavedHighlight(segment)
    } catch (error) {
      console.error(error)
      window.alert(error.message || t('favorite_failed', "Unable to update favorite. Please try again."))
      this.applySavedHighlight(segment)
    } finally {
      delete button.dataset.busy
      button.removeAttribute("aria-busy")
      BUSY_CLASSES.forEach(cls => button.classList.remove(cls))
      this.updateFavoriteState()
    }
  }

  cancelSelection() {
    const segment = this.selectionSegment
    this.clearSelectionState()
    if (segment) this.applySavedHighlight(segment)
    window.getSelection()?.removeAllRanges()
  }

  updateFavoriteState() {
    if (!this.favoriteEnabledValue || !this.hasFavoriteButtonTarget) return

    const button = this.favoriteButtonTarget
    const segment = this.currentSegment()
    if (!segment) {
      button.setAttribute("disabled", "disabled")
      button.setAttribute("aria-disabled", "true")
      button.title = "Unavailable"
      this.renderFavoriteButton(false)
      return
    }

    const favoritable = segment.dataset.favoritable === "true"
    const favorited = segment.dataset.favorited === "true"

    if (favoritable) {
      button.removeAttribute("disabled")
      button.setAttribute("aria-disabled", "false")
      button.title = favorited ? "Remove from Favorites" : "Save to Favorites"
    } else {
      button.setAttribute("disabled", "disabled")
      button.setAttribute("aria-disabled", "true")
      button.title = "Unavailable"
    }

    this.renderFavoriteButton(favorited)
    this.applySavedHighlight(segment)
  }

  renderFavoriteButton(favorited) {
    if (!this.hasFavoriteButtonTarget) return
    const button = this.favoriteButtonTarget
    button.innerHTML = favorited ? HEART_SOLID : HEART_OUTLINE
    button.setAttribute("aria-pressed", String(favorited))
    button.setAttribute("aria-label", favorited ? "Remove from Favorites" : "Save to Favorites")
    button.title = favorited ? "Remove from Favorites" : "Save to Favorites"
    button.classList.toggle("text-rose-600", favorited)
  }

  updateNavState() {
    const count = parseInt(this.element.querySelector("#mf-total").textContent, 10)
    const i = this.currentIndex()
    const prev = this.element.querySelector('[data-action="mf-viewer#prev"]')
    const next = this.element.querySelector('[data-action="mf-viewer#next"]')
    if (prev) {
      const disabled = i <= 0
      this.disableNavButton(prev, disabled, next)
    }
    if (next) {
      const disabled = i >= count - 1
      this.disableNavButton(next, disabled, prev)
    }

    this.hasReachedEnd ||= (i >= count - 1)
  }

  // Disabling a focused button drops focus to <body> in most browsers —
  // silently stranding a keyboard user. Hand focus to the sibling nav
  // button when it's still usable, or to the segment frame otherwise.
  disableNavButton(button, disabled, sibling) {
    const hadFocus = document.activeElement === button
    button.disabled = disabled
    button.setAttribute('aria-disabled', String(disabled))
    button.classList.toggle('invisible', disabled)

    if (disabled && hadFocus) {
      if (sibling && !sibling.disabled) {
        sibling.focus()
      } else {
        this.element.querySelector('#mf-frame')?.focus()
      }
    }
  }

  handleKeys(e) {
    if (document.querySelector("details[data-testid='paragraph-note-control'][open]")) return
    if (this.isEditableTarget(e.target)) return

    if (e.key === "ArrowRight" || e.key.toLowerCase() === "j") this.next()
    if (e.key === "ArrowLeft" || e.key.toLowerCase() === "k") this.prev()
    if (e.key === "1") this.react({ currentTarget: { dataset: { reaction: "up" } } })
    if (e.key === "2") this.react({ currentTarget: { dataset: { reaction: "neutral" } } })
    if (e.key === "3") this.react({ currentTarget: { dataset: { reaction: "down" } } })
  }

  // Single-key shortcuts (j/k/1/2/3) must not hijack typing in the note
  // textarea or any other form field on the page.
  isEditableTarget(target) {
    if (!target) return false
    if (target.isContentEditable) return true
    return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  }

  extractSelection(segment) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null
    const range = selection.getRangeAt(0)
    if (!segment.contains(range.commonAncestorContainer)) return null

    const sentences = this.sentencesForSegment(segment)
    if (sentences.length === 0) return null

    const overlapping = sentences.filter((sentence) => {
      try {
        return range.intersectsNode(sentence)
      } catch (_) {
        return false
      }
    })

    if (overlapping.length === 0) return null

    const sorted = overlapping.sort((a, b) => this.compareIds(a.dataset.sentId, b.dataset.sentId))
    const startId = sorted[0].dataset.sentId
    const endId = sorted[sorted.length - 1].dataset.sentId
    if (!startId || !endId) return null
    return { startId, endId }
  }

  sentencesForSegment(segment) {
    return Array.from(segment.querySelectorAll('.sentence[data-sent-id]'))
  }

  findSentenceById(segment, id) {
    if (!id) return null
    return segment.querySelector(`.sentence[data-sent-id="${this.escapeSelector(id)}"]`)
  }

  findSentenceIndex(sentences, id) {
    return sentences.findIndex((sentence) => sentence.dataset.sentId === id)
  }

  rangeLength() {
    if (!this.selectionSegment || !this.selection) return 0
    const sentences = this.sentencesForSegment(this.selectionSegment)
    const startIndex = this.findSentenceIndex(sentences, this.selection.startId)
    const endIndex = this.findSentenceIndex(sentences, this.selection.endId)
    if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) return 0
    return endIndex - startIndex + 1
  }

  canExtendSelection() {
    if (!this.selectionSegment || !this.selection) return false
    const sentences = this.sentencesForSegment(this.selectionSegment)
    const endIndex = this.findSentenceIndex(sentences, this.selection.endId)
    return endIndex !== -1 && endIndex < sentences.length - 1
  }

  highlightRange(segment, startId, endId, classes) {
    if (!segment || !startId || !endId) return
    const sentences = this.sentencesForSegment(segment)
    const startIndex = this.findSentenceIndex(sentences, startId)
    const endIndex = this.findSentenceIndex(sentences, endId)
    if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) return

    for (let idx = startIndex; idx <= endIndex; idx += 1) {
      classes.forEach((cls) => sentences[idx].classList.add(cls))
    }
  }

  clearHighlightClasses(segment, classes) {
    if (!segment) return
    this.sentencesForSegment(segment).forEach((sentence) => {
      classes.forEach((cls) => sentence.classList.remove(cls))
    })
  }

  clearActiveHighlight(segment) {
    this.clearHighlightClasses(segment, ACTIVE_CLASSES)
  }

  clearSavedHighlight(segment) {
    this.clearHighlightClasses(segment, SAVED_CLASSES)
  }

  applySavedHighlight(segment) {
    if (!segment) return
    this.clearSavedHighlight(segment)
    const start = this.normalizeId(segment.dataset.favoriteStart)
    const end = this.normalizeId(segment.dataset.favoriteEnd)
    if (!start || !end) return
    this.highlightRange(segment, start, end, SAVED_CLASSES)
  }

  clearSelectionState() {
    if (this.selectionControls) {
      this.selectionControls.remove()
      this.selectionControls = null
    }
    if (this.selectionSegment) {
      this.clearActiveHighlight(this.selectionSegment)
    }
    this.selection = null
    this.selectionSegment = null
  }

  normalizeId(id) {
    if (!id) return null
    if (id === "undefined" || id === "null") return null
    return id
  }

  compareIds(a, b) {
    const matchA = /p_(\d+)_s_(\d+)/.exec(a || '')
    const matchB = /p_(\d+)_s_(\d+)/.exec(b || '')
    if (!matchA || !matchB) return (a || '').localeCompare(b || '')
    const paraDiff = parseInt(matchA[1], 10) - parseInt(matchB[1], 10)
    if (paraDiff !== 0) return paraDiff
    return parseInt(matchA[2], 10) - parseInt(matchB[2], 10)
  }

  escapeSelector(value) {
    if (!value) return ''
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value)
    }
    return value.replace(/[.*+?^${}()|\[\]\\]/g, '\\$&')
  }
}
